import {
    Avatar, Box, Button, ButtonGroup, Dialog, Divider, Grid, List, ListItem, ListItemText,
    Paper, Tab, TableContainer, Tabs, Typography
} from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { ActiveUserLabel } from "./ActiveUserLabel";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLoggedUserId } from "../user/userSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { Localized, useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { selectSettings } from "../settings/settingsSlice";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { loadUserRequests, UserRequests } from "../userRequests/userRequestsApi";
import { UserRequestsStatusChip } from "../userRequests/UserRequestsStatusChip";
import { AddEditUserRequestDialog, AddEditUserRequestDialogProps } from "../userRequests/dialogs/AddEditUserRequestDialog";
import produce from "immer"
import { If } from "../../app/If";
import { UserRequestFilters, UserRequestsFilterPanel } from "../userRequests/UserRequestsFilterPanel";
import { defUserRequestsFilters } from "../userRequests/helper";
import { AppUser, loadAppUser } from "./administration";
import { ChangePersonalDataDialog, ChangePersonalDataDialogProps } from "./dialogs/changePersonalDataDialog";

type ProfileTabs = "settings" | "myReports";

type UserRequestDialogState =
    | { type: "none" }
    | { type: "changePersonalData", props: ChangePersonalDataDialogProps }
    | { type: "addUserRequest", props: AddEditUserRequestDialogProps }
    | { type: "editUserRequest", props: AddEditUserRequestDialogProps }
    | { type: "readUserRequest", props: AddEditUserRequestDialogProps }
    | { type: "deleteUserRequest", props: AddEditUserRequestDialogProps }

export const UserProfile = () => {
    const { locale } = useAppSelector(selectSettings);
    
    const loggedUserId = useAppSelector(selectLoggedUserId);
    const [user, setUser] = useState<AppUser | undefined>(undefined)
    const [tabValue, setTabValue] = useState<ProfileTabs>("settings");
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const navigate = useNavigate()
    const { l10n } = useLocalization();
    const [userRequests, setUserRequests] = useState<UserRequests[]>([]);
    const [userRequestsFiltered, setUserRequestsFiltered] = useState<UserRequests[]>([]);

    const [dialog, setDialog] = useState<UserRequestDialogState>({ type: "none" })

    const [filters, setFilters] = useState<UserRequestFilters>(defUserRequestsFilters);

    const handleChange = (_event: React.SyntheticEvent, newValue: ProfileTabs) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadAppUser(loggedUserId))
            .then(unwrapResult)
            .then(setUser)
            .catch((error) => { setIsError(true); setError(error); })
            .finally(() => setIsLoading(false));
        dispatch(loadUserRequests())
            .then(unwrapResult)
            .then(res => {
                setUserRequests(res);
                setUserRequestsFiltered(res);
            })
            .catch((error) => { setIsError(true); setError(error); })
    }, [dispatch, loggedUserId])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const reload = () => {
        const selectedFilters = filters.statusOptions.filter(filter => filter.selected).map(filter => filter.uniqueName);
        if(selectedFilters.length === 0) setUserRequestsFiltered(userRequests);
        else setUserRequestsFiltered(userRequests.filter(f => selectedFilters.includes(f.status)));
    }

    const handleChangePersonalData = useCallback(() => {
        if (!user) {
            return;
        }

        setDialog({
            type: "changePersonalData",
            props: {
                user,
                onSuccessfullySaved: (update: AppUser | undefined) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [user]);

    const handleAddUserRequest = useCallback(() => {
        setDialog({
            type: "addUserRequest",
            props: {
                userRequest: undefined,
                mode: "create",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const handleEditUserRequest = useCallback((userRequest: UserRequests) => {
        setDialog({
            type: "editUserRequest",
            props: {
                userRequest: userRequest,
                mode: "create",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const handleReadUserRequest = useCallback((userRequest: UserRequests) => {
        setDialog({
            type: "readUserRequest",
            props: {
                userRequest: userRequest,
                mode: "read",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const handleDeleteUserRequest = useCallback((userRequest: UserRequests) => {
        setDialog({
            type: "deleteUserRequest",
            props: {
                userRequest: userRequest,
                mode: "delete",
                onSuccessfullySaved: (update: UserRequests | number) => {
                    setDialog({ type: "none" });
                },
                onCancel: () => setDialog({ type: "none" })
            }
        });
    }, [setDialog]);

    const DialogContentDispatcher = ({ state, setter }: { state: UserRequestDialogState, setter: Dispatch<SetStateAction<UserRequestDialogState>> }) => {
        const onSuccessPersonalData = useCallback((user: AppUser | undefined) => {
            setter({ type: "none" })
            setUser(user);
        }, [setter])

        const onSuccess = useCallback((userRequest: UserRequests | number) => {
            setter({ type: "none" })
            setUserRequests(
                produce((draft) => {
                    if (draft !== undefined) {
                        if(typeof(userRequest) === "number") {
                            const existingIndex = draft.findIndex((group) => group.id === userRequest);

                            if (existingIndex !== -1) {
                                draft.splice(existingIndex, 1);
                            }
                        } else {
                            const existingIndex = draft.findIndex((group) => group.id === userRequest.id);

                            if (existingIndex !== -1) {
                                draft[existingIndex] = { ...draft[existingIndex], ...userRequest };
                            } else {
                                draft.push(userRequest);
                            }
                        }
                    }
                })
            );
        }, [setter])

        const onCancel = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (state.type) {
            case "none":
                return null

            case "changePersonalData":
                return <ChangePersonalDataDialog
                    user={state.props.user}
                    onSuccessfullySaved={onSuccessPersonalData}
                    onCancel={onCancel}
                />

            case "addUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={undefined}
                    mode={'create'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />

            case "editUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={state.props.userRequest}
                    mode={'edit'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />
            case "readUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={state.props.userRequest}
                    mode={'read'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />
            case "deleteUserRequest":
                return <AddEditUserRequestDialog
                    userRequest={state.props.userRequest}
                    mode={'delete'}
                    onSuccessfullySaved={onSuccess}
                    onCancel={onCancel}
                />
        }
    }

    const columns: GridColDef<UserRequests, any, any>[] = [
        {
            field: 'createDate',
            headerName: l10n.getString("create-date", null, "_Data utworzenia"),
            width: 300,
            valueGetter: (value) => {
                return new Date(value).toLocaleString();
            }
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => {
                return <UserRequestsStatusChip status={params.row.status}/>
            }
        },
        {
            field: 'actions', type: 'actions', width: 300,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        <If condition={params.row.status === "NEW"} otherwise={<Button color="primary" onClick={() => handleReadUserRequest(params.row)} >
                                <Localized id="details">
                                    <span>_Szczegóły</span>
                                </Localized>
                            </Button>}>
                            <Button color="primary" onClick={() => handleEditUserRequest(params.row)} >
                                <Localized id="edit">
                                    <span>_Edytuj</span>
                                </Localized>
                            </Button>
                            <Button color="error" onClick={() => handleDeleteUserRequest(params.row)} >
                                <Localized id="delete">
                                    <span>_Usuń</span>
                                </Localized>
                            </Button>
                        </If>
                    </ButtonGroup>
                )
            }
        }
    ]

    const redirectToSupportPage = () => {
        window.open(`${process.env.REACT_APP_SUPPORT}/UserBugs/CreateBug?t=${l10n.getString("error-unexpected-problem", null, "Wystąpił nieoczekiwany problem")}`, "_blank")
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    if (isError || !user) {
        return <>
            <ErrorContainer error={error} />
        </>
    }
    if ( !user) {
        return <>
           
        </>
    }

    return (
        <Box sx={{ backgroundColor: "#f5f5f5", minHeight: "90%", p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, textAlign: "center", height: "100%", borderRadius: 4 }}>
                        <Avatar
                            sx={{ width: 100, height: 100, margin: "0 auto" }}
                        />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            {user.firstName} {user.surname}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 1 }}>
                            <ActiveUserLabel />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <List>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-email">_email</Localized>} secondary={user.email} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-first-name">_first</Localized>} secondary={user.firstName} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-last-name">_last</Localized>} secondary={user.surname} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-phone">_phone</Localized>} secondary={user.phone} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-position">_pos</Localized>} secondary={user.position} />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Localized id="user-role" vars={{ num: 0 }}>_role</Localized>} secondary={user.role.toUpperCase()} />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={<Localized id="user-password-expiry-date">_expiryDate</Localized>}
                                    secondary={
                                        user.expiryDate
                                            ? new Date(user.expiryDate).toLocaleDateString()
                                            : "N/A"
                                    }
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    <Localized id="actions">_actions</Localized>
                                </Typography>
                                <Button fullWidth variant="contained" color="primary" sx={{ mt: 1 }} onClick={() => navigate(`/changepsw`)}>
                                    <Localized id="change-password">_password</Localized>
                                </Button>
                                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={handleChangePersonalData}>
                                    <Localized id="change-personal-data">_change-personal-data</Localized>
                                </Button>
                                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={handleAddUserRequest}>
                                    <Localized id="report-an-error-in-personal-data">_report-data</Localized>
                                </Button>
                                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={redirectToSupportPage}>
                                    <Localized id="report-an-error-in-system">_report-system</Localized>
                                </Button>

                            </Paper>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Tabs value={tabValue} onChange={handleChange} variant="fullWidth">
                                    <Tab label="Domyślne ustawienia" value="settings" />
                                    <Tab label="Moje zgłoszenia" value="myReports" />
                                </Tabs>
                                <Divider sx={{ my: 2 }} />
                                <Box>
                                    {tabValue === "settings" && (
                                        <Typography>
                                            Settings
                                        </Typography>
                                    )}
                                    {tabValue === "myReports" && (
                                        <>
                                            <UserRequestsFilterPanel filters={filters} onFilterChange={setFilters} reload={reload} />
                                            <TableContainer component={Paper}>
                                                <DataGrid localeText={getCustomLocaleText()}
                                                    style={{ minHeight: 100, height: 'calc(100vh - 700px)' }}
                                                    rows={userRequestsFiltered}
                                                    columns={columns}
                                                />
                                            </TableContainer>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                    variant="contained"
                    color="info"
                    size="medium"
                    onClick={() => navigate(-1)
                    }
                >
                    <Localized id="close">_close</Localized>
                </Button>
            </Box>
            <Dialog open={dialog.type !== "none"} fullWidth maxWidth={dialog.type === "changePersonalData" ? "sm" : "lg"} keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} />
            </Dialog>
        </Box>
    );
};
